import clsx from "clsx";
import React from "react";
import type { DetailData } from "../../../../../contexts/chat-log/chat-log-context";
import styles from "./styles.module.scss";

// YouTube IFrame Player API types
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

export default function Youtube({
  className,
  file_name,
  asset_title,
  text,
  fullWidth,
  ...props
}: Omit<React.ComponentPropsWithoutRef<"div">, "children"> &
  Pick<DetailData, "file_name" | "asset_title" | "text"> & { fullWidth?: boolean }) {
  const container = React.useRef<HTMLDivElement>(null);
  // fullWidth（単独表示）時のみサムネイル+再生ボタンのファサードから開始する
  const [playing, setPlaying] = React.useState(!fullWidth);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [showOverlay, setShowOverlay] = React.useState(true);
  const [showAnimation, setShowAnimation] = React.useState(true);
  // iframe読み込み完了まで内部の黒い背景を隠すためのフラグ
  const [isLoaded, setIsLoaded] = React.useState(false);

  // 4秒後にアニメーションを非表示
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowAnimation(false);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  // 透明オーバーレイをクリックして全画面表示
  const handleOverlayClick = () => {
    // まずオーバーレイを隠してワンタップで反応させる
    setShowOverlay(false);

    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
    const isSafari = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);

    if (isIOS || isSafari) return; // iOS/Safari ではフルスクリーン要求をスキップ

    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const fullscreenPromise = iframe.requestFullscreen?.() ||
        (iframe as any).webkitRequestFullscreen?.() ||
        (iframe as any).mozRequestFullScreen?.() ||
        (iframe as any).msRequestFullscreen?.();

      if (fullscreenPromise && (fullscreenPromise as any).then) {
        (fullscreenPromise as Promise<void>)
          .then(() => {
            if (iframe.contentWindow) {
              iframe.contentWindow.postMessage(
                JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
                '*'
              );
            }
          })
          .catch(() => {
            // 失敗は無視（オーバーレイは既に外れている）
          });
      }
    } catch {
      // 例外は無視
    }
  };


  // useGSAP(
  //   () => {
  //     gsap
  //       .timeline({ repeat: -1, repeatDelay: 2 })
  //       .to(".js-glow", {
  //         scale: 1.15,
  //         duration: 2,
  //         ease: "power1.inOut",
  //       })
  //       .to(
  //         ".js-glow",
  //         {
  //           scale: 1,
  //           duration: 2,
  //           ease: "power1.inOut",
  //         },
  //         "+=0.5"
  //       );
  //   },
  //   { scope: container2 }
  // );

  // useGSAP(
  //   () => {
  //     gsap
  //       .timeline({ repeat: -1, repeatDelay: 2 })
  //       .to(".js-glow", {
  //         scale: 1.15,
  //         duration: 2,
  //         ease: "power1.inOut",
  //       })
  //       .to(
  //         ".js-glow",
  //         {
  //           scale: 1,
  //           duration: 2,
  //           ease: "power1.inOut",
  //         },
  //         "+=0.5"
  //       );
  //   },
  //   { scope: container3 }
  // );

  return (
    <>
      <style>{`
        @keyframes whiteFlash {
          0%, 100% {
            border-color: rgba(200, 230, 255, 1);
            box-shadow: 0 0 15px rgba(100, 180, 255, 1), 
                        0 0 30px rgba(150, 210, 255, 1),
                        0 0 45px rgba(180, 220, 255, 0.9),
                        0 0 60px rgba(200, 230, 255, 0.7),
                        inset 0 0 15px rgba(180, 220, 255, 0.8);
          }
          50% {
            border-color: rgba(220, 240, 255, 0.4);
            box-shadow: 0 0 8px rgba(150, 200, 255, 0.6), 
                        0 0 15px rgba(180, 220, 255, 0.5),
                        0 0 22px rgba(200, 230, 255, 0.4),
                        0 0 30px rgba(220, 240, 255, 0.3),
                        inset 0 0 8px rgba(200, 230, 255, 0.4);
          }
        }
        .white-flash-border-youtube {
          animation: whiteFlash 1s ease-in-out infinite;
        }
      `}</style>
      {/* 中央揃えラッパー */}
      <div style={{ flexShrink: fullWidth ? 1 : 0, flexGrow: fullWidth ? 1 : 0, display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
        <div
          className={clsx(styles.youtube2, showAnimation && 'white-flash-border-youtube', className)}
          {...props}
          ref={container}
          style={{
            position: 'relative',
            width: fullWidth ? '80%' : '310px', // アセットカード2枚分の幅（190px×2 + gap 8px）の80%
            maxWidth: '100%',
            height: fullWidth ? 'auto' : '174px',
            aspectRatio: fullWidth ? '16 / 9' : undefined,
            margin: fullWidth ? '0 auto' : undefined,
            border: showAnimation ? "4px solid rgba(200, 230, 255, 1)" : "none",
            borderRadius: "12px",
            overflow: "hidden",
            isolation: "isolate",
            willChange: "auto",
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
            backgroundColor: 'transparent'
          }}
        >
          {fullWidth && !playing ? (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', padding: 0, cursor: 'pointer', background: 'none' }}
            >
              <img
                src={`https://img.youtube.com/vi/${file_name}/hqdefault.jpg`}
                alt={asset_title || '動画サムネイル'}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0) 50%)' }} />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '56px',
                  height: '56px',
                  borderRadius: '9999px',
                  background: 'rgba(255, 255, 255, 0.92)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.35)',
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#111" style={{ marginLeft: '3px' }}>
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              {(asset_title || text) && (
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '14px 16px', textAlign: 'left' }}>
                  {asset_title && (
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: '16px', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                      {asset_title}
                    </div>
                  )}
                  {text && (
                    <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', marginTop: '2px', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                      {text}
                    </div>
                  )}
                </div>
              )}
            </button>
          ) : (
            <>
              {/* 透明オーバーレイ - 最初のクリックで全画面表示 */}
              {showOverlay && (
                <div
                  onClick={handleOverlayClick}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 4,
                    cursor: 'pointer',
                    backgroundColor: 'transparent'
                  }}
                  title="クリックして全画面で再生"
                />
              )}
              <iframe
                id={`youtube-player-${file_name}`}
                ref={iframeRef}
                src={`https://www.youtube.com/embed/${file_name}?autoplay=${fullWidth ? 1 : 0}&mute=0&fs=1&enablejsapi=1`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={() => setIsLoaded(true)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  // 読み込み完了(onLoad)まで内部の黒い背景を隠し、フェードインで表示する
                  opacity: isLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease'
                }}
              ></iframe>
            </>
          )}
        </div>
      </div>
      {/* <div
        className={clsx(styles.youtube2, className)}
        {...props}
        ref={container2}
      >
        <div className={clsx(styles.glow2, "js-glow")} />
        <iframe
          width="560"
          height="315"
          src={`https://www.youtube.com/embed/${file_name}?autoplay=0&mute=1&fs=1`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        ></iframe>
      </div>
      <div
        className={clsx(styles.youtube2, className)}
        {...props}
        ref={container3}
      >
        <div className={clsx(styles.glow3, "js-glow")} />
        <iframe
          width="560"
          height="315"
          src={`https://www.youtube.com/embed/${file_name}?autoplay=0&mute=1&fs=1`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        ></iframe>
      </div> */}
    </>
  );
}
